export type Quest = {
  id: string;
  title: string;
  hostPath: string;
  iconSrc: string;
  tone: "mint" | "cyan" | "coral" | "violet" | "gold";
  detail: string;
  description: string;
  players: string;
  timing: string;
  steps: { title: string; description: string }[];
  details: string;
};

export const quests: Quest[] = [
  {
    id: "night-before-gfc",
    title: "Night Before GFC",
    hostPath: "/host/night-before-gfc",
    iconSrc: "/economy/roles/powell.webp",
    tone: "gold",
    detail: "One night before the crash.",
    description: "The Establishment vs the Populace. Play a secret financial role, move assets after dark, and vote out Powell before the rate hike triggers the GFC.",
    players: "3–12 players",
    timing: "One night, one debate, one vote",
    steps: [
      { title: "Take a secret role", description: "Choose a cast, then deal one card per player and three to the market. Keep your identity private." },
      { title: "Make your night move", description: "The announcer guides each role. Inspect, copy, or swap cards; everyone else taps Ready." },
      { title: "Stop the crash", description: "Trade stories, call bluffs, and vote. If no Powell is voted out, the GFC hits. Your final card decides whose side you’re on." },
    ],
    details: "Twelve role types, original financial satire, and no mid-game elimination. Everyone joins on their own device and talks in person or on a call. The host plays too.",
  },
  {
    id: "trivia",
    title: "Team Trivia",
    hostPath: "/host/trivia",
    iconSrc: "/icons/trivia.png",
    tone: "mint",
    detail: "Big brains. Shared discoveries.",
    description: "A quick brain break with a little friendly competition. Find out what your team knows—and who knows it best.",
    players: "Your whole team",
    timing: "10–90s per question",
    steps: [
      { title: "Pick your questions", description: "Start with a ready-made set or write your own, then share the room code." },
      { title: "Take the big screen", description: "The host presents the questions while players answer on their own devices." },
      { title: "See who knew", description: "Reveal the answer and follow the live leaders, scores, and room accuracy." },
    ],
    details: "The host runs the show without playing. Their screen displays the question, countdown, answers received, and live scoreboard—ready for a room display or screen share.",
  },
  {
    id: "random-draw",
    title: "Random Draw",
    hostPath: "/host/random-draw",
    iconSrc: "/icons/random-draw.png",
    tone: "cyan",
    detail: "Pick now or schedule it.",
    description: "Who’s up next? Turn picking people into a moment with falling marbles, a shuffled deck, or a spin of the wheel.",
    players: "1–100 names",
    timing: "Now or on a schedule",
    steps: [
      { title: "Bring your people", description: "Paste names, separated by commas. Then choose how many people you want in the lineup." },
      { title: "Let chance choose", description: "Use Pascal Drop for 2–10 names, Shuffle Deck for any team, or the wheel for one winner." },
      { title: "Make it a ritual", description: "Draw now or schedule daily, weekly, or monthly picks in your chosen time zone. Share a public results link." },
    ],
    details: "No repeats within a draw. Each schedule runs three times, then pauses until its host resumes it. Past results stay on the public link.",
  },
  {
    id: "two-truths",
    title: "Two Truths & a Lie",
    hostPath: "/host/two-truths",
    iconSrc: "/icons/two-truths.png",
    tone: "coral",
    detail: "Tell stories. Spot the fiction.",
    description: "Two real stories. One convincing invention. Discover the surprising things you never knew about the people on your team.",
    players: "2–12 players",
    timing: "Timed speaking & voting",
    steps: [
      { title: "Bring three statements", description: "Everyone privately writes two truths and one lie. No question pack needed." },
      { title: "Take the spotlight", description: "Share your stories on your timed turn. Everyone else votes for the statement they think is made up." },
      { title: "Reveal the fiction", description: "Uncover the lie, see who you fooled, and pass the spotlight to the next storyteller." },
    ],
    details: "A correct guess earns 100 points. The storyteller gets 100 points for every wrong guess. Everyone, including the host, gets a turn.",
  },
  {
    id: "odd-one-out",
    title: "The Odd One Out",
    hostPath: "/host/odd-one-out",
    iconSrc: "/icons/odd-one-out.png",
    tone: "violet",
    detail: "Secret words, clues & suspicion",
    description: "Almost everyone has the same secret word. A few have a related one. Listen closely: the smallest clue could give them away.",
    players: "4–12 players",
    timing: "Timed speaking & voting",
    steps: [
      { title: "Keep your word secret", description: "Receive a private word from an English word pack. Nobody is told which side they’re on." },
      { title: "Give a clue, in turn", description: "Describe your word aloud without saying it. After everyone speaks, vote for the odd one out." },
      { title: "Follow your suspicions", description: "The most-voted player is out; a tie saves everyone. Find every spy before they match the others in number." },
    ],
    details: "There’s one spy with 4–6 players, or two with 7–12. Pick a general, banking, insurance, property, AI, holiday, pop culture, or NSFW word pack.",
  },
  {
    id: "sketch-whisper",
    title: "Sketch Whisper",
    hostPath: "/host/sketch-whisper",
    iconSrc: "/icons/sketch-whisper.png",
    tone: "gold",
    detail: "Write, draw, mutate.",
    description: "Write a sentence, pass it on, draw what you read, then describe what you see. The last page is never the first idea.",
    players: "3–12 players",
    timing: "Timed writing & drawing",
    steps: [
      { title: "Write a drawable sentence", description: "Everyone starts a story with one short prompt. No artistic skill required." },
      { title: "Draw, then describe", description: "You only see the previous page. Sketch it, or write what the sketch looks like." },
      { title: "Flip through the chain", description: "When every book is full, replay each story from the first sentence to the last scribble." },
    ],
    details: "3–12 players, including the host. Writing and drawing turns are timed. Blank pages are used if someone runs out of time.",
  },
];

// Links shared before the rename keep working.
const RENAMED_QUESTS: Record<string, string> = { "rba-vs-economy": "night-before-gfc" };

export function getQuest(id: string | null): Quest | undefined {
  const current = id ? RENAMED_QUESTS[id] ?? id : id;
  return quests.find((quest) => quest.id === current);
}

export function getQuestForHostPath(path: string): Quest | undefined {
  const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, "");
  if (pathname === "/host/spy") return getQuest("odd-one-out");
  if (pathname === "/host/rba-vs-economy") return getQuest("night-before-gfc");
  return quests.find((quest) => quest.hostPath === pathname);
}
